import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Form, Button, Row, Col, Card } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FadeInUp, FadeInLeft, FadeInRight, ScrollFadeIn, ScrollStaggerContainer, StaggerItem } from './MotionWrappers';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../assets/css/psearch.css';
import LandingPageTrending from './LandingPageTrending';
import TrendingBrands from './TrendingBrands';
import TryOn from './TryOn';
import SEO from './SEO';
import apiClient from '../config/config';
import ReactGA from 'react-ga';
import { debounce } from 'lodash';
import { supabase } from '../supabaseClient';
import Nav from './Nav';
import SignInModal from './SignInModal';
import { useCurrency } from '../context/CurrencyContext';
import { useToast } from '../context/ToastContext';
import BrandSpotlight from './BrandSpotlight';
import UpcomingSpotlight from './UpcomingSpotlight';

ReactGA.initialize('G-SW7M3XVNPW'); 
ReactGA.pageview(window.location.pathname + window.location.search);

function HomePages() {
  const navigate = useNavigate();
  const { currency, getCurrencySymbol } = useCurrency();
  const { success } = useToast();
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [brands, setBrands] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnImage, setTryOnImage] = useState(null);
  const [user, setUser] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  const [pendingTryOnImage, setPendingTryOnImage] = useState(null);
  const [authEmail, setAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [savedProducts, setSavedProducts] = useState([]);
  const [featuredBrands, setFeaturedBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [showEmailSuccessModal, setShowEmailSuccessModal] = useState(false);

  const normalizeProductId = useCallback((productId) => {
    if (productId === null || productId === undefined) return '';
    return String(productId);
  }, []);

  // Helper function to properly capitalize product titles (title case)
  const capitalizeTitle = useCallback((title) => {
    if (!title) return '';
    
    // Words that should remain lowercase in title case
    const lowerCaseWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'with', 'as'];
    
    return title
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        // Always capitalize first word and words not in the lowerCaseWords list
        if (index === 0 || !lowerCaseWords.includes(word)) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word; // Keep lowercase words lowercase
      })
      .join(' ');
  }, []);

  // Check for user authentication
  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

    
  const handleTryOn = (image) => {
    if (!user) {
      setSignInModalMessage('Sign in to try it on and save your look.');
      setPendingTryOnImage(image);
      setShowSignInModal(true);
      return;
    }
    setTryOnImage(image);
    setTryOnOpen(true);
  };

  const handleGoogleSignIn = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        console.error('Error signing in:', error.message);
        alert(`Sign in error: ${error.message}`);
      } else {
        console.log('OAuth initiated:', data);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      alert(`Unexpected error: ${err.message}`);
    }
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error.message);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!authEmail) return;
    
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithOtp({
      email: authEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the magic link!');
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignUp = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { user_role: 'general' },
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email to confirm your account!');
    }
    setAuthLoading(false);
  };

  // Open try-on after successful sign in if there was a pending image
  useEffect(() => {
    if (user && pendingTryOnImage) {
      setTryOnImage(pendingTryOnImage);
      setTryOnOpen(true);
      setPendingTryOnImage(null);
      setShowSignInModal(false);
    }
  }, [user, pendingTryOnImage]);

  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length > 2) {
        await handleSearch();
      }
    }, 300),
    []
  );

  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => {
        setTimeoutReached(true);
        setLoading(false);
      }, 8000);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const handleCategorySelect = async (selection) => {
  
    setSelectedCategory(selection);
    setLoading(true);
    setError(null);
    setSearchPerformed(true);
    setTimeoutReached(false);
  
    try {
      const params = {
        category: selection === 'All' ? '' : selection,
        q: query || undefined,
        currency: currency,
      };
  
      const response = await apiClient.get('/products/search/', {
        params: params,
      });
  
      setProducts(response.data);
      setProductCount(response.data.length); 
    } catch (err) {
      setError('Failed to fetch products. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductsByBrand = async (brand) => {
    if (loading || selectedBrand === brand) return; 
    setLoading(true);
    setError(null);
    setSearchPerformed(true);
    setTimeoutReached(false);
  
    try {
      const response = await apiClient.get('/products/search/', {
        params: { brand },
        currency: currency,
      });
  
      setProducts(response.data);
      setProductCount(response.data.length); 
      setSelectedBrand(brand); 
    } catch (err) {
      setError('Failed to fetch products. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    // If no query, return all products
    if (!query || query.trim() === '') {
      return products;
    }
    // Otherwise filter by query
    return products.filter(product => 
      product.title?.toLowerCase().includes(query.toLowerCase()) ||
      product.brand?.toLowerCase().includes(query.toLowerCase())
    );
  }, [products, query]);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);
    setSearchPerformed(true);
    setTimeoutReached(false);
  
    try {
      const params = {
        q: query || undefined,
        category: selectedCategory === 'All' ? '' : selectedCategory,
        brand: selectedBrand || undefined,
        currency: currency,
      };
  
      const response = await apiClient.get('/products/search/', { params });
  
      setProducts(response.data);
      setProductCount(response.data.length);
    } catch (err) {
      setError('Failed to fetch products. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetSearch = () => {
    setSearchPerformed(false);
    setProducts([]);
    setQuery('');
    setSelectedCategory('All');
    setProductCount(0);
  };

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await apiClient.get('/brands/');
        if (response.data) {
          // Brands to exclude
          const excludedBrands = ['Shop Namastay', 'Baraqah', 'Koshur', 'Ethnic Wears'];
          
          // Filter out excluded brands, trim whitespace, and remove duplicates (case-insensitive)
          const seen = new Set();
          const filteredBrands = response.data
            .map(brand => brand ? brand.trim() : brand)  // Trim whitespace/newlines
            .filter(brand => {
              if (!brand) return false;
              const normalizedBrand = brand.toLowerCase().trim();
              if (seen.has(normalizedBrand)) return false;
              if (excludedBrands.some(excluded => excluded.toLowerCase() === normalizedBrand)) return false;
              seen.add(normalizedBrand);
              return true;
            });
          
          setBrands(filteredBrands);
        }
      } catch (err) {
        console.error('Error fetching brands:', err);
      }
    };

    fetchBrands();
  }, []);

  useEffect(() => {
    if (searchPerformed) {
      handleSearch();
    }
  }, [currency]); 
  

  useEffect(() => {
    if (user) {
      loadSavedProducts(user.id);
    } else {
      setSavedProducts([]);
    }
  }, [user]);

  useEffect(() => {
    fetchFeaturedBrands();
  }, []);

  const fetchFeaturedBrands = async () => {
    setBrandsLoading(true);
    try {
      // Fetch brands with images
      const brandsResponse = await apiClient.get('/brands/with-images/');
      const allBrands = brandsResponse.data || [];
      const brandsMap = new Map();
      allBrands.forEach(brand => {
        brandsMap.set(brand.name, brand);
      });

      // Fetch saved brands count from Supabase (real data)
      let brandSaveCounts = {};
      try {
        const { data: savedBrandsData, error } = await supabase
          .from('saved_brands')
          .select('brand_name');
        
        if (!error && savedBrandsData) {
          savedBrandsData.forEach(item => {
            const brandName = item.brand_name;
            brandSaveCounts[brandName] = (brandSaveCounts[brandName] || 0) + 1;
          });
        }
      } catch (err) {
        console.error('Error fetching saved brands:', err);
      }

      // Get 5 most liked brands (using real save counts)
      const brandsWithCounts = allBrands.map(brand => ({
        ...brand,
        saveCount: brandSaveCounts[brand.name] || 0,
      }));

      const mostLiked = [...brandsWithCounts]
        .filter(b => b.saveCount > 0)
        .sort((a, b) => {
          if (b.saveCount !== a.saveCount) {
            return b.saveCount - a.saveCount;
          }
          return (a.name || '').localeCompare(b.name || '');
        })
        .slice(0, 5); // Top 5 most liked

      const mostLikedNames = new Set(mostLiked.map(b => b.name));

      // Get 5 recently added brands from products table (using real created_at data)
      let recentlyAddedBrands = [];
      try {
        const { data: recentProducts, error: productsError } = await supabase
          .from('products')
          .select('brand, image, created_at')
          .not('brand', 'is', null)
          .neq('brand', '')
          .order('created_at', { ascending: false })
          .limit(100); // Get recent products to find unique brands

        if (!productsError && recentProducts) {
          // Get unique brands from recent products, ordered by most recent product
          const brandFirstSeen = new Map(); // brand -> { image, created_at }
          
          recentProducts.forEach(product => {
            const brandName = product.brand?.trim();
            if (brandName && !mostLikedNames.has(brandName) && !brandFirstSeen.has(brandName)) {
              brandFirstSeen.set(brandName, {
                name: brandName,
                image: product.image,
                created_at: product.created_at,
              });
            }
          });

          // Convert to array and sort by created_at (most recent first)
          recentlyAddedBrands = Array.from(brandFirstSeen.values())
            .sort((a, b) => {
              const dateA = new Date(a.created_at || 0);
              const dateB = new Date(b.created_at || 0);
              return dateB - dateA;
            })
            .slice(0, 5) // Top 5 recently added
            .map(brandData => {
              // Use image from brandsMap if available, otherwise use product image
              const brandFromMap = brandsMap.get(brandData.name);
              return {
                name: brandData.name,
                image: brandFromMap?.image || brandData.image,
                saveCount: brandSaveCounts[brandData.name] || 0,
              };
            });
        }
      } catch (err) {
        console.error('Error fetching recently added brands:', err);
      }

      // Combine: 5 most liked + 5 recently added = 10 total
      const combined = [...mostLiked, ...recentlyAddedBrands];
      
      setFeaturedBrands(combined);
    } catch (err) {
      console.error('Error fetching featured brands:', err);
      setFeaturedBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  };

  const loadSavedProducts = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('saved_products')
        .select('product_id')
        .eq('user_id', userId);
      if (data && !error) {
        setSavedProducts(data.map(item => normalizeProductId(item.product_id)).filter(Boolean));
      }
    } catch (error) {
      console.error('Error loading saved products:', error);
    }
  };

  const toggleSaveProduct = async (productId) => {
    if (!user) {
      setSignInModalMessage('Sign in to save your favorite product.');
      setShowSignInModal(true);
      return;
    }
    const normalizedProductId = normalizeProductId(productId);
    if (!normalizedProductId) return;
    const isSaved = savedProducts.includes(normalizedProductId);
    try {
      if (isSaved) {
        await supabase
          .from('saved_products')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', normalizedProductId);
        setSavedProducts(savedProducts.filter(id => id !== normalizedProductId));
      } else {
        await supabase
          .from('saved_products')
          .insert({ user_id: user.id, product_id: normalizedProductId });
        setSavedProducts([...savedProducts, normalizedProductId]);
      }
    } catch (error) {
      console.error('Error toggling saved product:', error);
    }
  };

  return (
    <Container className="py-5">
      <SEO
        title="Discover Kashmiri Fashion"
        description="Discover and shop authentic Kashmiri fashion online. Browse pashmina shawls, phiran, aari embroidery, and handcrafted Koshur clothing from 50+ Kashmiri brands with AI virtual try-on."
        path="/"
      />
      <Nav/>

      {/* Hero Section — full-bleed editorial */}
      <div style={{
        position: 'relative',
        marginTop: '-20px',
        marginBottom: '0',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        height: '92vh',
        minHeight: '560px',
        maxHeight: '860px',
        overflow: 'hidden',
      }}>
        {/* Background image with subtle Ken Burns zoom */}
        <motion.img
          src={require('../assets/images/pinkshawl.webp')}
          alt="Kashmiri Fashion"
          loading="eager"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 8, ease: 'linear' }}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 35%',
          }}
        />

        {/* Dark gradient overlay — heavier at bottom for text legibility */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.72) 100%)',
        }} />

        {/* Overlay content */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          padding: '0 5vw 60px',
        }}>
          {/* Logo */}
          {/* <motion.img
            src={require('../assets/images/frontlogo.png')}
            alt="Yemberzal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            style={{
              width: '260px',
              height: 'auto',
              marginBottom: '20px',
              filter: 'brightness(0) invert(1)',
              pointerEvents: 'none',
            }}
            className="hero-logo-overlay"
          /> */}

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              color: '#fff',
              lineHeight: 1.1,
              margin: '0 0 16px 0',
              letterSpacing: '-0.01em',
              maxWidth: '680px',
              textTransform: 'uppercase',
            }}
          >
            Kashmir's Finest<br />Fashion, Curated.
          </motion.h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '0.95rem',
              color: 'rgba(255,255,255,0.82)',
              lineHeight: 1.7,
              margin: '0 0 32px 0',
              maxWidth: '460px',
              fontWeight: 400,
            }}
          >
            Pashmina, Phiran, Aari embroidery &amp; more — from artisans and designers who call Kashmir home.
          </motion.p>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.65 }}
            style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}
          >
            <Link
              to="/shop"
              style={{
                display: 'inline-block',
                padding: '14px 32px',
                backgroundColor: '#fff',
                color: '#000',
                borderRadius: '2px',
                textDecoration: 'none',
                fontSize: '12px',
                fontWeight: '700',
                fontFamily: 'Montserrat, sans-serif',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                transition: 'all 0.25s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Shop Now
            </Link>
            <Link
              to="/brands"
              style={{
                display: 'inline-block',
                padding: '14px 32px',
                backgroundColor: 'transparent',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.7)',
                borderRadius: '2px',
                textDecoration: 'none',
                fontSize: '12px',
                fontWeight: '600',
                fontFamily: 'Montserrat, sans-serif',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                transition: 'all 0.25s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Browse Brands
            </Link>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          style={{
            position: 'absolute',
            bottom: '24px',
            right: '5vw',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '10px',
            fontFamily: 'Montserrat, sans-serif',
            letterSpacing: '2px',
            textTransform: 'uppercase',
          }}
        >
          <span>Scroll</span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 1.4, ease: 'easeInOut' }}
            style={{ width: '1px', height: '28px', background: 'rgba(255,255,255,0.5)' }}
          />
        </motion.div>
      </div>

      {/* Stats strip */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0',
          padding: '20px 0',
          borderBottom: '1px solid #ececec',
          marginBottom: '48px',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)',
          flexWrap: 'wrap',
        }}
      >
        {[
          { value: '1300+', label: 'Products' },
          { value: '9', label: 'Brands' },
          { value: '100%', label: 'Kashmiri' },
          { value: 'AI', label: 'Virtual Try-On' },
        ].map((stat, i) => (
          <div key={i} style={{
            flex: '1 1 120px',
            textAlign: 'center',
            padding: '12px 24px',
            borderRight: i < 3 ? '1px solid #ececec' : 'none',
          }}>
            <div style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '1.4rem',
              fontWeight: 700,
              color: '#1a1a1a',
              lineHeight: 1,
              marginBottom: '4px',
            }}>{stat.value}</div>
            <div style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '10px',
              color: '#999',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
            }}>{stat.label}</div>
          </div>
        ))}
      </motion.div>

      {/* Category pill quick-browse */}
      

      {/* CSS for responsive styles */}
      <style>{`
        .hero-logo-overlay {
          width: 260px;
        }
        @media (max-width: 768px) {
          .hero-logo-overlay {
            width: 180px !important;
          }
        }

        /* Notify section mobile */
        @media (max-width: 768px) {
          .notify-section {
            flex-direction: column !important;
            gap: 20px !important;
            padding: 30px 0 !important;
            margin-top: 60px !important;
            text-align: center;
          }
          .notify-heading {
            white-space: normal !important;
          }
          .notify-divider {
            display: none !important;
          }
        }

        /* Section headings mobile */
        @media (max-width: 480px) {
          .section-heading-row {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 8px !important;
          }
        }
      `}</style>

      
      {searchPerformed && (
        <div className="search-results">
          {error && <div className="alert alert-danger">{error}</div>}
          {timeoutReached && !error && (
            <div className="text-center">
              <p>Search is taking longer than expected. Please try again.</p>
              <Button onClick={resetSearch} variant="outline-secondary">
                Most Liked
              </Button>
            </div>
          )}
          {filteredProducts.length === 0 && !error && !timeoutReached && (
            <div className="text-center">
              <Button onClick={resetSearch} variant="outline-secondary">
                Most Liked
              </Button>
            </div>
          )}
          <div className="text-center mb-4">
            <strong>{filteredProducts.length} products found</strong>
          </div>
          <Row>
            {filteredProducts.map((product, index) => (
              <Col md={4} key={index} className="mb-4">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                <Card 
                  className="glass-card"
                  style={{ height: '650px', width: '90%', align: 'center', borderRadius: '25px', overflow: 'hidden', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', cursor: 'pointer' }}
                  onClick={() => navigate(`/product/${product.id || encodeURIComponent(product.link)}`)}
                >
                  <div style={{ height: '70%', overflow: 'hidden', position: 'relative' }}>
                    {product.image && (
                      <img 
                        src={product.image} 
                        alt={product.title || 'Product Image'} 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',  
                          objectPosition: 'center top',
                          filter: product.is_out_of_stock ? 'grayscale(40%)' : 'none',
                        }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/default-image.avif';
                        }}
                      />
                    )}
                    {/* Out of stock overlay */}
                    {product.is_out_of_stock && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,0.32)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        zIndex: 5,
                      }}>
                        <span style={{
                          background: '#fff',
                          color: '#1a1a1a',
                          fontSize: '10px',
                          fontWeight: '700',
                          letterSpacing: '2px',
                          textTransform: 'uppercase',
                          padding: '6px 14px',
                          fontFamily: 'Montserrat, sans-serif',
                        }}>
                          Out of Stock
                        </span>
                      </div>
                    )}
                    <button
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '18px',
                        background: 'rgba(255,255,255,0.95)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!user) {
                          setShowSignInModal(true);
                          return;
                        }
                        toggleSaveProduct(product.id ?? product.link);
                      }}
                      title={savedProducts.includes(normalizeProductId(product.id ?? product.link)) ? 'Remove from saved' : 'Save product'}
                    >
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill={savedProducts.includes(normalizeProductId(product.id ?? product.link)) ? '#e63946' : 'none'}
                        stroke="#e63946"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 21s-7-4.35-7-10A5 5 0 0 1 12 6a5 5 0 0 1 7 5c0 5.65-7 10-7 10z" />
                      </svg>
                    </button>
                    <button
                      style={{
                        position: 'absolute',
                        bottom: '12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        padding: '8px 14px',
                        borderRadius: '999px',
                        border: 'none',
                        background: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        backdropFilter: 'blur(6px)',
                      }}
                      onClick={(e) => { e.stopPropagation(); handleTryOn(product.image); }}
                    >
                      Try it on
                    </button>
                  </div>
                  
                  <Card.Body className="d-flex flex-column" style={{ padding: '15px' }}>
                    <Card.Text className="yb-brand-name" style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                      <strong>{product.brand || 'Unknown Brand'}</strong>
                    </Card.Text>
                    <Card.Title style={{ fontSize: '16px', color: '#566' }}>
                      {capitalizeTitle(product.title) || 'Unnamed Product'}
                    </Card.Title>
                    <Card.Text style={{ fontSize: '16px', color: '#555' }}>
                    {getCurrencySymbol()} {product.price || 'Not Available'}
                    </Card.Text>

                    {/* Category Labels */}
                    {product.category && product.category !== 'uncategorized' && (
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '6px', 
                        marginTop: '10px',
                        marginBottom: '10px'
                      }}>
                        {product.category.split(/, | & /).filter(cat => cat.trim()).map((cat, index) => (
                          <span
                            key={index}
                            className="yb-category-tag"
                            style={{
                              backgroundColor: '#f0f0f0',
                              color: '#333',
                              padding: '4px 10px',
                              borderRadius: '15px',
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
                </motion.div>
              </Col>
            ))}
          </Row>

          {/* Render TryOn ONCE */}
          {tryOnOpen && user && (
            <TryOn
              productImage={tryOnImage}
              onClose={() => setTryOnOpen(false)}
              user={user}
            />
          )}
        </div>
      )}

      {!searchPerformed && (
        <>
          {/* Brand Spotlight - Shows newly launched brands */}
          <BrandSpotlight />
          
          {/* Upcoming Spotlight - Shows brands launching soon */}
          <UpcomingSpotlight />

          {/* Shop by Vibe - Editorial mood-based collections */}
          <ScrollFadeIn delay={0.1}>
            <div style={{ marginTop: '80px', marginBottom: '80px' }}>
              {/* Editorial section header */}
              <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    fontFamily: 'Montserrat, sans-serif',
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '3px',
                    marginBottom: '12px',
                  }}
                >
                  Curated Collections
                </motion.p>
                <motion.h2
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  style={{
                    fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
                    fontWeight: 300,
                    fontFamily: 'Montserrat, sans-serif',
                    color: '#1a1a1a',
                    letterSpacing: '-0.02em',
                    margin: 0,
                  }}
                >
                  Shop by Vibe
                </motion.h2>
              </div>

              {/* Vibe grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px',
                maxWidth: '1200px',
                margin: '0 auto',
              }}>
                {[
                  { title: 'Timeless Elegance', desc: 'Classic pieces that never go out of style', category: 'Pashmina', img: require('../assets/images/pashm-shawl.jpg') },
                  { title: 'Contemporary Chic', desc: 'Modern silhouettes with traditional craft', category: 'Suits', img: require('../assets/images/black-shawl.jpg') },
                  { title: 'Heritage Luxe', desc: 'Artisan embroidery and handwoven textiles', category: 'Shawls', img: require('../assets/images/shawl.avif') },
                  { title: 'Effortless Grace', desc: 'Flowing fabrics for everyday elegance', category: 'Pheran', img: require('../assets/images/pheran.jpg') },
                ].map((vibe, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    onClick={() => handleCategorySelect(vibe.category)}
                    style={{
                      position: 'relative',
                      height: '420px',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    }}
                    whileHover={{ y: -6 }}
                  >
                    <img
                      src={vibe.img}
                      alt={vibe.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      onError={(e) => {
                        e.currentTarget.src = '/default-image.avif';
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      padding: '28px',
                    }}>
                      <h3 style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '1.3rem',
                        fontWeight: 500,
                        color: '#fff',
                        margin: '0 0 8px 0',
                        letterSpacing: '-0.01em',
                      }}>
                        {vibe.title}
                      </h3>
                      <p style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '0.85rem',
                        color: 'rgba(255,255,255,0.85)',
                        margin: '0 0 12px 0',
                        lineHeight: 1.5,
                      }}>
                        {vibe.desc}
                      </p>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 600,
                        color: '#fff',
                        textTransform: 'uppercase',
                        letterSpacing: '1.5px',
                      }}>
                        Explore <span style={{ fontSize: '14px' }}>→</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollFadeIn>

          {/* Featured Collection Banner - Full-width editorial */}
          <ScrollFadeIn delay={0.1}>
            <div style={{
              marginLeft: 'calc(-50vw + 50%)',
              marginRight: 'calc(-50vw + 50%)',
              marginBottom: '80px',
              position: 'relative',
              height: '500px',
              overflow: 'hidden',
            }}>
              <img
                src={require('../assets/images/pinkshawl.webp')}
                alt="Featured Collection"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
                onError={(e) => {
                  e.currentTarget.src = '/default-image.avif';
                }}
              />
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(90deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)',
                display: 'flex',
                alignItems: 'center',
                padding: '0 8vw',
              }}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                  style={{ maxWidth: '540px' }}
                >
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.8)',
                    textTransform: 'uppercase',
                    letterSpacing: '3px',
                    marginBottom: '16px',
                  }}>
                    New Arrivals
                  </p>
                  <h2 style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: 'clamp(2rem, 4vw, 3.2rem)',
                    fontWeight: 300,
                    color: '#fff',
                    lineHeight: 1.2,
                    margin: '0 0 20px 0',
                    letterSpacing: '-0.02em',
                  }}>
                    The Artisan<br />Collection
                  </h2>
                  <p style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '1rem',
                    color: 'rgba(255,255,255,0.9)',
                    lineHeight: 1.7,
                    marginBottom: '32px',
                  }}>
                    Handcrafted pieces celebrating Kashmir's rich heritage of embroidery and weaving traditions.
                  </p>
                  <Link
                    to="/shop"
                    style={{
                      display: 'inline-block',
                      padding: '14px 32px',
                      backgroundColor: '#fff',
                      color: '#000',
                      borderRadius: '2px',
                      textDecoration: 'none',
                      fontSize: '11px',
                      fontWeight: 700,
                      fontFamily: 'Montserrat, sans-serif',
                      letterSpacing: '2px',
                      textTransform: 'uppercase',
                      transition: 'all 0.25s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Discover Now
                  </Link>
                </motion.div>
              </div>
            </div>
          </ScrollFadeIn>

          {/* {featuredBrands.length > 0 && (
            <div style={{ marginBottom: '48px' }}>
              <h2 className="text-center mb-4" style={{ fontSize: '24px', fontWeight: '400', color: '#1a1a1a' }}>
                Most Liked Brands
              </h2>
              {brandsLoading ? (
                <div className="text-center" style={{ padding: '20px', color: '#888' }}>
                  Loading brands...
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: '',
                    gap: '0px',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '0 20px',
                  }}
                >
                  {featuredBrands.map((brand, index) => (
                    <div
                      key={index}
                      onClick={() => navigate(`/brand/${encodeURIComponent(brand.name)}`)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '20px',
                        width: '180px',
                        // background: '#faf9f8',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        // transition: 'all 0.2s ease',
                        // border: '1px solid transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f5f3f0';
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#faf9f8';
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {brand.image ? (
                        <img
                          src={brand.image}
                          alt={brand.name}
                          style={{
                            width: '150px',
                            height: '200px',
                            borderRadius: '12px',
                            objectFit: 'fill-box',
                            flexShrink: 0,
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/default-image.avif';
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '150px',
                            height: '150px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: '#999',
                            fontSize: '24px',
                            fontWeight: '600',
                          }}
                        >
                          {brand.name?.charAt(0)?.toUpperCase() || 'B'}
                        </div>
                      )}
                      <div style={{ textAlign: 'center', width: '100%' }}>
                        <div
                          className="yb-brand-name"
                          style={{
                            fontSize: '16px',
                            fontWeight: '500',
                            marginBottom: '6px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {brand.name}
                        </div>
                        {brand.saveCount > 0 && (
                          <div
                            style={{
                              fontSize: '12px',
                              // color: '#888',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="#e63946" stroke="#e63946" strokeWidth="2">
                              <path d="M12 21s-7-4.35-7-10A5 5 0 0 1 12 6a5 5 0 0 1 7 5c0 5.65-7 10-7 10z" />
                            </svg>
                            {brand.saveCount} {brand.saveCount === 1 ? 'save' : 'saves'}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )} */}

          {/* Most Liked Outfits */}
          <ScrollFadeIn delay={0.1}>
          <div style={{ marginBottom: '40px', marginTop: '80px' }}>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              style={{
                fontSize: '11px',
                fontWeight: '600',
                fontFamily: 'Montserrat, sans-serif',
                color: '#999',
                textTransform: 'uppercase',
                letterSpacing: '3px',
                margin: '0 0 12px 0',
              }}
            >
              What's Trending
            </motion.p>
            <div className="d-flex justify-content-between align-items-end">
              <motion.h2
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{
                  fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
                  fontWeight: 300,
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#1a1a1a',
                  letterSpacing: '-0.02em',
                  margin: 0,
                }}
              >
                Most Loved Pieces
              </motion.h2>
              <Link 
                to="/shop" 
                className="text-decoration-none"
                style={{ 
                  color: '#1a1a1a', 
                  fontSize: '11px',
                  fontWeight: '600',
                  fontFamily: 'Montserrat, sans-serif',
                  borderBottom: '1px solid #1a1a1a',
                  paddingBottom: '2px',
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                }}
              >
                View All →
              </Link>
            </div>
          </div>
          </ScrollFadeIn>
          <LandingPageTrending
            query={query}
            onTryOn={handleTryOn}
            user={user}
            savedProducts={savedProducts}
            toggleSaveProduct={toggleSaveProduct}
            setShowSignInModal={setShowSignInModal}
            limit={10}
          />

          {/* Trending Brands */}
          <ScrollFadeIn delay={0.1}>
          <div style={{ marginBottom: '40px', marginTop: '80px' }}>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              style={{
                fontSize: '11px',
                fontWeight: '600',
                fontFamily: 'Montserrat, sans-serif',
                color: '#999',
                textTransform: 'uppercase',
                letterSpacing: '3px',
                margin: '0 0 12px 0',
              }}
            >
              Discover
            </motion.p>
            <div className="d-flex justify-content-between align-items-end">
              <motion.h2
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{
                  fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
                  fontWeight: 300,
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#1a1a1a',
                  letterSpacing: '-0.02em',
                  margin: 0,
                }}
              >
                Trending Brands
              </motion.h2>
              <Link 
                to="/brands" 
                className="text-decoration-none"
                style={{ 
                  color: '#1a1a1a', 
                  fontSize: '11px',
                  fontWeight: '600',
                  fontFamily: 'Montserrat, sans-serif',
                  borderBottom: '1px solid #1a1a1a',
                  paddingBottom: '2px',
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                }}
              >
                View All →
              </Link>
            </div>
          </div>
          </ScrollFadeIn>
          <TrendingBrands />
        </>
      )}

      {/* TryOn for trending products (when not searched) */}
      {!searchPerformed && tryOnOpen && user && (
        <TryOn
          productImage={tryOnImage}
          onClose={() => setTryOnOpen(false)}
          user={user}
        />
      )}

      {/* Stay Notified Section */}
      {!searchPerformed && (
        <div className="notify-section" style={{
          borderTop: '1px solid #ddd',
          borderBottom: '1px solid #ddd',
          padding: '40px 0',
          marginTop: '100px',
          marginBottom: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '40px',
          flexWrap: 'wrap',
        }}>
          <h3 className="notify-heading" style={{
            fontSize: '14px',
            fontWeight: '400',
            fontFamily: 'Montserrat, sans-serif',
            color: '#1a1a1a',
            margin: 0,
            lineHeight: 1.4,
          }}>
            Sign up to stay notified when new brands launch.
          </h3>
          <div className="notify-divider" style={{
            width: '1px',
            height: '80px',
            backgroundColor: '#ddd',
          }} />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const email = e.target.elements.notifyEmail.value;
              if (!email) return;
              fetch(process.env.REACT_APP_GOOGLE_EMAIL_SIGNUP_SCRIPTS_URL, {
                method: 'POST',
                mode: 'no-cors',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, timestamp: new Date().toISOString() }),
              });
              e.target.elements.notifyEmail.value = '';
              setShowEmailSuccessModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              maxWidth: '500px',
            }}
          >
            <input
              name="notifyEmail"
              type="email"
              placeholder="Enter your email address"
              required
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '14px',
                fontFamily: 'Montserrat, sans-serif',
                color: '#333',
                backgroundColor: 'transparent',
                padding: '4px 0',
              }}
            />
            <button
              type="submit"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </form>
        </div>
      )}

      {/* Sign In Modal */}
      {/* Email Signup Success Modal */}
      {showEmailSuccessModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowEmailSuccessModal(false)}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '40px 20px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ 
              fontSize: '16px', 
              marginBottom: '20px',
              color: '#1a1a1a',
              fontFamily: "'Montserrat', sans-serif"
            }}>
              Thanks for Subscribing!
            </p>
            <button
              onClick={() => setShowEmailSuccessModal(false)}
              style={{
                padding: '10px 30px',
                background: '#1a1a1a',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: "'Montserrat', sans-serif"
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <SignInModal
        open={showSignInModal}
        onClose={() => {
          setShowSignInModal(false);
          setAuthMessage('');
          setAuthEmail('');
          setSignInModalMessage('');
        }}
        message={signInModalMessage}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authLoading={authLoading}
        authMessage={authMessage}
        onEmailSignIn={handleEmailSignIn}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailPasswordSignIn={handleEmailPasswordSignIn}
        onEmailPasswordSignUp={handleEmailPasswordSignUp}
      />
    </Container>
  );
}

export default HomePages;